import { TradePackage, TradeCard } from 'store/services/trade/tradeApi';
import { getLanguageDisplayName } from 'constants/languages';

export const ROW_LIMIT = 10_000;

// Condition abbreviations in grade order (best to worst)
const CONDITION_ORDER = ['NEAR_MINT', 'SLIGHTLY_PLAYED', 'MODERATELY_PLAYED', 'HEAVILY_PLAYED', 'DAMAGED'];
const CONDITION_ABBREVIATIONS: Record<string, string> = {
  NEAR_MINT: 'NM',
  SLIGHTLY_PLAYED: 'LP',
  MODERATELY_PLAYED: 'MP',
  HEAVILY_PLAYED: 'HP',
  DAMAGED: 'DMG',
};

interface CsvRow {
  cardName: string;
  points: string;
  quantity: number;
  setName: string;
  setCode: string;
  finish: string;
  condition: string;
  language: string;
  trader: string;
  profileUrl: string;
}

const CSV_HEADER = 'Card Name,Points,Quantity,Set Name,Set Code,Finish,Condition,Language,Trader,Profile URL';

function buildFinishString(card: TradeCard): string {
  const parts: string[] = [];
  if (card.acceptFoil) parts.push('Foil');
  if (card.acceptNonFoil) parts.push('Nonfoil');
  if (parts.length === 0) return 'Foil, Nonfoil';
  return parts.join(', ');
}

function buildSetCodeString(card: TradeCard): string {
  if (card.acceptableSetDetails && card.acceptableSetDetails.length > 0) {
    return card.acceptableSetDetails.map(d => d.code.toUpperCase()).join(', ');
  }
  if (card.acceptableSets && card.acceptableSets.length > 0) {
    return card.acceptableSets.join(', ');
  }
  return 'Any';
}

function buildSetNameString(card: TradeCard): string {
  if (card.acceptableSetDetails && card.acceptableSetDetails.length > 0) {
    return card.acceptableSetDetails.map(d => d.name).join(', ');
  }
  if (card.acceptableSets && card.acceptableSets.length > 0) {
    return card.acceptableSets.join(', ');
  }
  return 'Any';
}

function buildConditionString(card: TradeCard): string {
  const conditions = card.acceptableConditions;
  if (!conditions || conditions.length === 0) return 'Any';

  const condSet = new Set(conditions.map(c => c.toUpperCase()));
  if (condSet.size >= CONDITION_ORDER.length) return 'Any';

  return CONDITION_ORDER
    .filter(c => condSet.has(c))
    .map(c => CONDITION_ABBREVIATIONS[c] || c)
    .join(', ');
}

function buildLanguageString(card: TradeCard): string {
  const languages = card.acceptableLanguages;
  if (!languages || languages.length === 0) return 'Any';

  return languages
    .map(l => getLanguageDisplayName(l))
    .join(', ');
}

export function generateExplodedCsvRows(
  packages: TradePackage[]
): { rows: CsvRow[]; truncated: boolean } {
  const rows: CsvRow[] = [];

  for (const pkg of packages) {
    for (const card of pkg.cards) {
      if (card.quantity <= 0) continue;
      if (rows.length >= ROW_LIMIT) {
        return { rows, truncated: true };
      }
      rows.push({
        cardName: card.name,
        points: card.pointValue != null && card.pointValue > 0 ? String(card.pointValue) : '???',
        quantity: card.quantity,
        setName: buildSetNameString(card),
        setCode: buildSetCodeString(card),
        finish: buildFinishString(card),
        condition: buildConditionString(card),
        language: buildLanguageString(card),
        trader: pkg.username,
        profileUrl: `https://trademagic.gg/profile/u/${pkg.username.toLowerCase()}`,
      });
    }
  }

  return { rows, truncated: false };
}

function escapeField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function formatCsvContent(rows: CsvRow[]): string {
  const lines = rows.map(row =>
    [
      escapeField(row.cardName),
      row.points,
      String(row.quantity),
      escapeField(row.setName),
      escapeField(row.setCode),
      escapeField(row.finish),
      escapeField(row.condition),
      escapeField(row.language),
      escapeField(row.trader),
      row.profileUrl,
    ].join(',')
  );
  return [CSV_HEADER, ...lines].join('\n');
}

export function downloadCsv(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
